import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, ButtonGroup, Dropdown, Form } from "react-bootstrap";
import { getCurrentUser, listDataEntities } from "../services/apiClient.js";
import EntityManager from "../components/systemData/EntityManager.jsx";
import QuoteFeeProfilesPanel from "../components/systemData/QuoteFeeProfilesPanel.jsx";
import OrderFeeProfilesPanel from "../components/systemData/OrderFeeProfilesPanel.jsx";
import QuoteNoteTemplatesManager from "../components/systemData/QuoteNoteTemplatesManager.jsx";
import QuoteTemplatesManager from "../components/systemData/QuoteTemplatesManager.jsx";
import SystemSettingsPanel from "../components/systemData/SystemSettingsPanel.jsx";
import SystemNotices from "../components/systemData/SystemNotices.jsx";
import ErrorLogPanel from "../components/systemData/ErrorLogPanel.jsx";
import SchedulerPanel from "../components/systemData/SchedulerPanel.jsx";
import UpgradeListManager from "../components/systemData/UpgradeListManager.jsx";
import WhosOnlinePanel from "../components/systemData/WhosOnlinePanel.jsx";
import ManageUsers from "../components/admin/ManageUsers.jsx";

// The System Data View: pick an entity, manage its rows.
//   Buttons    — the office-maintained lookups (companies, rooms, …).
//   Dark pair  — System Users + System Settings (people/behaviour, not lookups;
//                Settings is admin-only, with an owner section inside).
//   Dark menu  — Owner Data: every ownerOnly entity, SYSTEM OWNER only. These
//                are the structural lookups users abused in 1.0 ("think of a
//                feature, hack it in via a dropdown") — writes are also
//                enforced server-side (requireEntityWriteAccess).

const SYSTEM_USERS_KEY = "system-users";
const QUOTE_NOTE_TEMPLATES_KEY = "quote-note-templates"; // bespoke manager, not an EntityManager entity
const QUOTE_TEMPLATES_KEY = "quote-templates"; // ditto — ordered lines, and the priced ones carry rates
// An ENTITY key (the generic grid is half the tab, and ?entity= deep links
// into it keep working), rendered through a composing panel that adds the
// fee registries beneath the grid.
const QUOTE_FEE_PROFILES_KEY = "quote-fee-profiles";
// Same shape for orders (migration 024): the entity grid plus the five order
// registries behind a "Show" radio. ONE tab — orders have no text templates,
// so there is no Order Templates to pair it with.
const ORDER_FEE_PROFILES_KEY = "order-fee-profiles";
const SETTINGS_KEY = "system-settings";
const ERROR_LOG_KEY = "error-log"; // admin: what has actually broken, and for whom
const SCHEDULER_KEY = "scheduler"; // owner: the after-hours tasks + switches
const UPGRADE_LIST_KEY = "upgrade-list"; // admin: the upgrade backlog, KS/KR priority order
const WHOS_ONLINE_KEY = "whos-online"; // owner: who is on now, who worked today (presence.md)

// TWO LEVELS (Robert 2026-09-05): one row of seventeen buttons filled a
// 2,300px screen and wrapped on a laptop, with Orders about to add more. The
// top row is the QUESTION (a category), the second row its answers — the
// same "question, then answers" shape Quote Templates and the Fee Profiles
// tabs use. The category is DERIVED from the selected tab, so ?entity= deep
// links and F5 keep working with no new state. A `direct` category is one
// tab that deserves its own button (Order Fee Profiles); `dark` = the
// people-and-behaviour set, kept apart from the lookups. Keys not present in
// the catalogue are skipped; the ownerOnly entities live in Owner Data.
const CATEGORIES = [
  { key: "lookups", label: "Lookups", keys: ["job-categories", "weather-events", "variation-reasons", "floors", "rooms"] },
  // Work Centres sit WITH the contractor companies they describe (KS
  // 2026-08-28, moving them out of Owner Data: admins add trades in 1.0).
  { key: "companies", label: "Companies & Trades", keys: ["assessor-companies", "insurer-companies", "contractor-companies", "work-centres", "assessors"] },
  // Quote Fee Profiles is the whole quote-fee configuration on one tab — the
  // profile cascade grid and, behind a "Show" radio, the P&G / JMF / Profit /
  // Rubbish registries (the registries were extra "Fills" radios on Quote
  // Templates from 2026-08-19 until 2026-09-05).
  { key: "quotes", label: "Quotes", keys: [QUOTE_NOTE_TEMPLATES_KEY, QUOTE_TEMPLATES_KEY, QUOTE_FEE_PROFILES_KEY] },
  { key: "orders", label: "Order Fee Profiles", keys: [ORDER_FEE_PROFILES_KEY], direct: true },
  // People + behaviour — dark, and FLAT: each tab is its own button on the
  // top row rather than behind a "System" category (Robert, same day: put
  // the System buttons back). (File Import, the 1.0 bulk file pull, sat here
  // until go-live was done — removed 2026-09-05.)
  { key: "system", label: "System", keys: [SYSTEM_USERS_KEY, SETTINGS_KEY, SCHEDULER_KEY, ERROR_LOG_KEY, UPGRADE_LIST_KEY, WHOS_ONLINE_KEY], dark: true, flat: true },
];
const DARK_KEYS = new Set([SYSTEM_USERS_KEY, SETTINGS_KEY, SCHEDULER_KEY, ERROR_LOG_KEY, UPGRADE_LIST_KEY, WHOS_ONLINE_KEY]);

function SystemDataPage() {
  // Deep-link support: /admin/system-data?entity=<key>&edit=<rowId> opens an
  // entity (and a row's editor) directly — used by the H&S PL Expiry Alert.
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [entities, setEntities] = useState([]);
  const [selectedKey, setSelectedKey] = useState(searchParams.get("entity") || null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [error, setError] = useState("");
  const [me, setMe] = useState(null);

  const isOwner = !!me?.is_system_owner;
  const isAdmin = !!me?.is_admin;

  // The selected entity RIDES THE URL (replace, not push — the back button
  // should leave the page, not replay every tab click), so F5 lands back on
  // the tab being viewed instead of resetting to the first one (Robert
  // 2026-08-19). Same ?entity= the deep links already use.
  useEffect(() => {
    if (!selectedKey) return;
    if (searchParams.get("entity") === selectedKey) return;
    setSearchParams({ entity: selectedKey }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey]);

  // …and the URL steers the panel back: the initializer above reads ?entity=
  // once, at mount, so a navigation that changes it while ALREADY here (the
  // header's critical-error badge deep-links to the error log) changed the
  // address bar and nothing else. Both effects guard on equality, so they
  // hand over rather than loop.
  useEffect(() => {
    const fromUrl = searchParams.get("entity");
    if (fromUrl && fromUrl !== selectedKey) setSelectedKey(fromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    listDataEntities()
      .then((data) => {
        setEntities(data);
        setSelectedKey((current) => current || data.find((e) => !e.ownerOnly)?.key || null);
      })
      .catch((err) =>
        setError(err.response?.data?.error || "Unable to load system data.")
      );
    getCurrentUser().then(setMe).catch(() => {});
  }, []);

  function labelFor(key) {
    if (key === SYSTEM_USERS_KEY) return "System Users";
    if (key === SETTINGS_KEY) return isAdmin ? "System Settings" : null;
    if (key === SCHEDULER_KEY) return isOwner ? "Scheduler" : null;
    if (key === ERROR_LOG_KEY) return isAdmin ? "Error Log" : null;
    if (key === UPGRADE_LIST_KEY) return isAdmin ? "Upgrade List" : null;
    if (key === WHOS_ONLINE_KEY) return isOwner ? "Who's Online" : null;
    if (key === QUOTE_NOTE_TEMPLATES_KEY) return "Quote Note Templates";
    if (key === QUOTE_TEMPLATES_KEY) return "Quote Templates";
    const entity = entities.find((e) => e.key === key);
    if (!entity || entity.ownerOnly) return null; // owner entities live in the dropdown
    return entity.label || null;
  }

  const ownerEntities = isOwner ? entities.filter((e) => e.ownerOnly) : [];
  const selectedEntity = entities.find((entity) => entity.key === selectedKey);
  // A category's visible tabs (labelFor hides what this user may not see).
  const categoryItems = (cat) =>
    cat.keys.map((key) => ({ key, label: labelFor(key) })).filter((item) => item.label);
  const activeCategory = CATEGORIES.find((cat) => cat.keys.includes(selectedKey)) || null;
  // Deep-links can't sidestep the owner-only entities.
  const selectionAllowed = selectedEntity ? !(selectedEntity.ownerOnly && !isOwner) : true;
  const onUsers = selectedKey === SYSTEM_USERS_KEY;
  const onSettings = selectedKey === SETTINGS_KEY && isAdmin;
  const onScheduler = selectedKey === SCHEDULER_KEY && isOwner;
  const onErrorLog = selectedKey === ERROR_LOG_KEY && isAdmin;
  const onUpgradeList = selectedKey === UPGRADE_LIST_KEY && isAdmin;
  const onWhosOnline = selectedKey === WHOS_ONLINE_KEY && isOwner;
  const onQuoteNoteTemplates = selectedKey === QUOTE_NOTE_TEMPLATES_KEY;
  const onQuoteTemplates = selectedKey === QUOTE_TEMPLATES_KEY;

  return (
    <div>
        <div className="d-flex flex-wrap align-items-center column-gap-4 mb-3">
          <h4 className="mb-0">System Data View</h4>
        </div>

        <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
          {/* Level 1 — the category. Clicking one lands on its first tab;
              a direct category IS its tab. Spaced, not a joined group
              (Robert): these are five different questions, not one list. */}
          <div className="d-flex flex-wrap gap-2">
            {CATEGORIES.flatMap((cat) => {
              const items = categoryItems(cat);
              if (items.length === 0) return [];
              // A flat category puts each of its tabs straight on this row.
              if (cat.flat) {
                return items.map((item) => (
                  <Button
                    key={item.key}
                    size="sm"
                    variant={item.key === selectedKey ? "dark" : "outline-dark"}
                    className="fw-semibold"
                    onClick={() => setSelectedKey(item.key)}
                  >
                    {item.label}
                  </Button>
                ));
              }
              const active = activeCategory?.key === cat.key;
              return [
                <Button
                  key={cat.key}
                  size="sm"
                  variant={cat.dark ? (active ? "dark" : "outline-dark") : active ? "primary" : "outline-primary"}
                  className={cat.dark ? "fw-semibold" : undefined}
                  onClick={() => { if (!active || cat.direct) setSelectedKey(items[0].key); }}
                >
                  {cat.label}
                </Button>,
              ];
            })}
          </div>

          {/* Owner Data — the structural lookups, System Owner only. */}
          {ownerEntities.length > 0 ? (
            <Dropdown>
              <Dropdown.Toggle
                size="sm"
                variant={selectedEntity?.ownerOnly ? "dark" : "outline-dark"}
                className="fw-semibold"
                id="owner-data-menu"
              >
                {selectedEntity?.ownerOnly ? `Owner Data: ${selectedEntity.label}` : "Owner Data"}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {ownerEntities.map((e) => (
                  <Dropdown.Item
                    key={e.key}
                    active={e.key === selectedKey}
                    onClick={() => setSelectedKey(e.key)}
                  >
                    {e.label}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          ) : null}

          {/* System Users gets this too (Robert 2026-08-03): 1.0's users page has
              always had it, and 2.0's list was showing every account ever
              imported with no way to hide the ones it had switched off. */}
          {!onQuoteNoteTemplates && !onQuoteTemplates && !onSettings && !onScheduler && !onErrorLog && !onUpgradeList ? (
            <Form.Check
              type="checkbox"
              id="sd-show-inactive"
              className="ms-auto mb-0"
              label="Show inactive records"
              checked={includeInactive}
              onChange={(event) => setIncludeInactive(event.target.checked)}
            />
          ) : null}
        </div>

        {/* Level 2 — the category's tabs, the buttons the row used to be. */}
        {activeCategory && !activeCategory.direct && !activeCategory.flat ? (
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
            <ButtonGroup size="sm">
              {categoryItems(activeCategory).map((item) => (
                <Button
                  key={item.key}
                  variant={
                    DARK_KEYS.has(item.key)
                      ? item.key === selectedKey
                        ? "dark"
                        : "outline-dark"
                      : item.key === selectedKey
                        ? "primary"
                        : "outline-primary"
                  }
                  className={DARK_KEYS.has(item.key) ? "fw-semibold" : undefined}
                  onClick={() => setSelectedKey(item.key)}
                >
                  {item.label}
                </Button>
              ))}
            </ButtonGroup>
          </div>
        ) : (
          <div className="mb-3" />
        )}

        {/* Silent-decay warnings (holiday horizon etc.) — invisible when healthy. */}
        <SystemNotices />

        {error ? <div className="alert alert-danger">{error}</div> : null}

        {onUsers ? (
          <ManageUsers includeInactive={includeInactive} />
        ) : onSettings ? (
          <SystemSettingsPanel />
        ) : onScheduler ? (
          <SchedulerPanel />
        ) : onErrorLog ? (
          <ErrorLogPanel />
        ) : onUpgradeList ? (
          <UpgradeListManager isOwner={isOwner} />
        ) : onWhosOnline ? (
          <WhosOnlinePanel />
        ) : onQuoteNoteTemplates ? (
          <QuoteNoteTemplatesManager canWrite={isAdmin} />
        ) : onQuoteTemplates ? (
          <QuoteTemplatesManager canWrite={isAdmin} />
        ) : selectedEntity && selectedEntity.key === ORDER_FEE_PROFILES_KEY ? (
          <OrderFeeProfilesPanel
            key={selectedEntity.key}
            entity={selectedEntity}
            canWrite={isAdmin}
            includeInactive={includeInactive}
            initialEditId={
              selectedEntity.key === searchParams.get("entity")
                ? Number(searchParams.get("edit")) || null
                : null
            }
            onDeepLinkDone={() => navigate(-1)}
          />
        ) : selectedEntity && selectedEntity.key === QUOTE_FEE_PROFILES_KEY ? (
          <QuoteFeeProfilesPanel
            key={selectedEntity.key}
            entity={selectedEntity}
            canWrite={isAdmin}
            includeInactive={includeInactive}
            initialEditId={
              selectedEntity.key === searchParams.get("entity")
                ? Number(searchParams.get("edit")) || null
                : null
            }
            onDeepLinkDone={() => navigate(-1)}
          />
        ) : selectedEntity && selectionAllowed ? (
          <EntityManager
            key={selectedEntity.key}
            entity={selectedEntity}
            includeInactive={includeInactive}
            initialEditId={
              selectedEntity.key === searchParams.get("entity")
                ? Number(searchParams.get("edit")) || null
                : null
            }
            onDeepLinkDone={() => navigate(-1)}
          />
        ) : null}
    </div>
  );
}

export default SystemDataPage;
